import React from 'react';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AbsencesIcon from '@mui/icons-material/BeachAccess';
import ReportsIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import TimelineIcon from '@mui/icons-material/Timeline';
import TimesheetsIcon from '@mui/icons-material/AccessTime';
import PlanningIcon from '@mui/icons-material/ViewKanban';
import DirectoryIcon from '@mui/icons-material/ContactPage';
import AssistantIcon from '@mui/icons-material/AutoAwesome';
import { P, hasPermission } from '../utils/permissions';

export type NavGroup = 'WORKSPACE' | 'TIME' | 'INSIGHTS';

export const GROUP_LABELS: Record<NavGroup, string> = {
  WORKSPACE: 'Workspace',
  TIME: 'Time',
  INSIGHTS: 'Insights',
};

export interface NavItem {
  id: string;
  label: string;
  icon: React.ReactElement;
  path: string;
  group: NavGroup;
  /** Returns true when this item should be visible for the given permission set. */
  gate: (perms: string[]) => boolean;
}

export interface NavSection {
  group: NavGroup;
  label: string;
  items: NavItem[];
}

const always = () => true;

export const NAV: NavItem[] = [
  { id: 'dashboard',            label: 'Dashboard',           icon: <DashboardIcon />,  path: '/dashboard',           group: 'WORKSPACE', gate: always },
  { id: 'people',               label: 'People',              icon: <PeopleIcon />,     path: '/people',              group: 'WORKSPACE', gate: (p) => hasPermission(p, P.MANAGE_USERS) },
  { id: 'projects',             label: 'Projects',            icon: <AssignmentIcon />, path: '/projects',            group: 'WORKSPACE', gate: (p) => hasPermission(p, P.VIEW_PROJECTS) },
  { id: 'project-timeline',     label: 'Project Timeline',    icon: <TimelineIcon />,   path: '/project-timeline',    group: 'WORKSPACE', gate: always },
  { id: 'consultant-timeline',  label: 'Consultant Timeline', icon: <GroupsIcon />,     path: '/consultant-timeline', group: 'WORKSPACE', gate: always },
  { id: 'planning',             label: 'Planning Board',      icon: <PlanningIcon />,   path: '/planning',            group: 'WORKSPACE', gate: (p) => hasPermission(p, P.MANAGE_ALLOCATIONS) },
  { id: 'directory',            label: 'Directory',           icon: <DirectoryIcon />,  path: '/directory',           group: 'WORKSPACE', gate: always },
  { id: 'timesheets',           label: 'Timesheets',          icon: <TimesheetsIcon />, path: '/timesheets',          group: 'TIME',      gate: always },
  { id: 'team-absences',        label: 'Team Time Off',       icon: <AbsencesIcon />,   path: '/team-absences',       group: 'TIME',      gate: always },
  { id: 'assistant',            label: 'Assistant',           icon: <AssistantIcon />,  path: '/assistant',           group: 'INSIGHTS',  gate: (p) => hasPermission(p, P.USE_AI_ASSISTANT) },
  { id: 'reports',              label: 'Reports',             icon: <ReportsIcon />,    path: '/reports',             group: 'INSIGHTS',  gate: (p) => hasPermission(p, P.VIEW_REPORTS_OPERATIONAL) },
  { id: 'admin',                label: 'Admin',               icon: <SettingsIcon />,   path: '/admin',               group: 'INSIGHTS',  gate: (p) => hasPermission(p, P.MANAGE_SETTINGS) },
];

const NAV_GROUP_ORDER: NavGroup[] = ['WORKSPACE', 'TIME', 'INSIGHTS'];

/**
 * Returns the nav grouped by section, with items filtered to those the user
 * has permission to see. Empty sections are omitted.
 */
export function visibleNav(perms: string[]): NavSection[] {
  return NAV_GROUP_ORDER
    .map((group) => ({
      group,
      label: GROUP_LABELS[group],
      items: NAV.filter((item) => item.group === group && item.gate(perms)),
    }))
    .filter((section) => section.items.length > 0);
}
